import React from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Responsezero from "./Responsezero";
import { base_URL } from "../../config/config";
const formatYAxis = (value) => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value;
};

// Utility function for formatting time
const formatTime = (minutes) => {
  if (minutes < 60) return `${minutes} mins`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes
    ? `${hours} hrs ${remainingMinutes} mins`
    : `${hours} hrs`;
};

const Responserate = ({ startDate, endDate, venueName }) => {
  const vendorId =
    localStorage.getItem("invitedBy") || localStorage.getItem("vendorId");

  const {
    data: analyticsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "responseRateAnalytics",
      startDate,
      endDate,
      venueName,
      vendorId,
    ],
    queryFn: async () => {
      const response = await axios.get(
        `${base_URL}/api/dashboard/response-rate/${vendorId}`,
        {
          params: {
            startDate: startDate?.toISOString().split("T")[0],
            endDate: endDate?.toISOString().split("T")[0],
            venueName,
          },
        }
      );
      return response.data.data; // Expecting `data` field in the response
    },
  });

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { time, percentageChange, month } = payload[0]?.payload;
      return (
        <div className="bg-white p-2 border border-gray-200 rounded-md shadow-sm">
          <p className="text-[10px] text-[#B0B0B0] font-medium">{month}</p>
          <div className="text-[12px] text-[#222222] flex items-center gap-1">
            {formatTime(time)}
            {percentageChange !== null && (
              <span
                className={`text-[9px] p-1 rounded-lg ml-1 ${
                  percentageChange > 0
                    ? "text-green-700 bg-green-100"
                    : "text-red-700 bg-red-100"
                }`}
              >
                {percentageChange > 0 ? "+" : ""}
                {percentageChange}%
              </span>
            )}
          </div>
        </div>
      );
    }
    return null;
  };
  if (isLoading)
    return (
      <div className="p-4 bg-white rounded-2xl animate-pulse bg-black/20"></div>
    );
  if (error || !analyticsData || analyticsData.length === 0)
    return <Responsezero />;

  const averageResponseTimeFormatted = formatTime(
    Math.round(
      analyticsData.reduce((sum, item) => sum + item.time, 0) /
        analyticsData.length
    )
  );

  return (
    <div className="w-full bg-white rounded-xl md:w-[35%] ">
      <div className="p-4">
        <div className="space-y-0 pb-2">
          <div className="flex items-baseline gap-2">
            <p className="text-[20px] md:text-[28px] font-bold">
              {averageResponseTimeFormatted}
            </p>
            <span className="text-[14px] md:text-[16px] text-[#222222]">
              Response time
            </span>
          </div>
          <p className="text-[12px] md:text-[14px] text-[#717171]">
            Response time from your team
          </p>
        </div>
        <div className="h-[220px] w-full mt-7">
          <ResponsiveContainer
            width="100%"
            height={window.innerWidth < 1000 ? "100%" : "125%"}
          >
            <AreaChart
              data={analyticsData}
              margin={{ top: 5, right: 0, bottom: 5, left: -10 }}
            >
              <defs>
                <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#DFDFFF" />
                  <stop offset="95%" stopColor="#DFDFFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#E5E7EB"
              />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#717171" }} />
              <YAxis
                tick={{ fontSize: 12, fill: "#717171" }}
                tickFormatter={formatYAxis}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="time"
                stroke="#7A79FF"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorGradient)"
                strokeDasharray="3 3"
                dot={false}
                activeDot={{
                  r: 6,
                  fill: "#4F46E5",
                  stroke: "#FFFFFF",
                  strokeWidth: 2,
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Responserate;
